import * as THREE from 'three';
import type { Viewer } from '../../../../viewer/viewer';
import type { IPersona, IViewConfig } from '../../../types';
import { ViewModes } from '../../../helpers';
import { PointoramaRoom } from '../../../../utils/PointoramaRoom';
import { getPointCloudIntersection } from '../../modules/shared/ScenePicking';
import type { AnnotationKindAdapter, CommitContext, HitTestContext } from './AnnotationKindAdapter';
import type { EditableAnnotation } from '../../modules/MeasureEngine';
import type { EditableShape } from './EditableShape';
import type { BasePlaneState, SelectionContextProvider } from './SelectionContextProvider';
import { FloorSelectionContext } from './FloorSelectionContext';
import { detectRoomVolume } from '../../../../utils/floorDetection/roomVolume';

/**
 * The original room flow lifted out of LineTool and the line modes.
 * Behavior matches the prior inline code:
 *   - 3D: edge raycast (with 1.1× point-cloud occlusion) + interior
 *     point-in-polygon fallback (also occluded).
 *   - 2D: edge raycast only, no occlusion (the cloud and rooms sit at the
 *     same floor height in top-down ortho so distance-based occlusion
 *     would falsely reject every room).
 *
 * Commit payloads (create_room, room_updated) are unchanged so existing
 * usePotreeEvents handlers keep working.
 */
export class RoomKindAdapter implements AnnotationKindAdapter {
  /** Room tools may only edit rooms — not openings/surfaces (also AREA geometry). */
  readonly editDomain = 'room' as const;

  readonly polygonPipelineConfigPath: string | null = null;

  /** Rooms: height/Z-tuned depth pipeline with cluster-merge (pre eye-space refactor). */
  readonly depthPipelineConfigPath: string | null = '/config/segmentation/room-depth-pipeline-config.json';

  /** Rooms: shift OPTS INTO 90° snap (freeform drag is the default). */
  readonly invertShiftFor90Snap = false;

  /**
   * Rooms: enable the radial edge-op menu (parallel / perpendicular / fixed-distance),
   * but NOT the dashed-line constraint snap — those are independent flags. The auto and
   * manual room flows share this adapter, so both get the menu.
   */
  readonly enableEdgeOpMenu = true;

  /** Rooms: prompt-handle editing between region selection and commit is part of the workflow. */
  readonly autoCommitNewFromRegion = false;

  /**
   * The floor selection context this adapter owns. `commitNew` reads ceiling
   * height + floor group id off it (those aren't on the kind-neutral
   * `basePlane`); set in `createSelectionContext`.
   */
  private floorContext: FloorSelectionContext | null = null;

  createSelectionContext(viewer: Viewer, persona: IPersona): SelectionContextProvider {
    this.floorContext = new FloorSelectionContext(viewer, persona);
    return this.floorContext;
  }

  getViewConfig(): IViewConfig {
    return {
      views: [ViewModes.XRAY_2D, ViewModes.PERSPECTIVE_3D_RGB],
      defaultViewId: ViewModes.XRAY_2D.id,
    };
  }

  hitTestEditableTarget(ctx: HitTestContext): EditableShape | null {
    const { viewer, mouse, coordinateTransformer } = ctx;
    const camera = viewer.scene.getActiveCamera();
    const is3D = camera.type === 'PerspectiveCamera';

    const activeFloorId = this.floorContext?.getGroupIdentifier() ?? null;

    // Edge raycast first. The InputHandler keeps its raycaster aimed at
    // the current mouse on every move, so we use it directly.
    const fromEdgeHit = this.findRoomFromEdgeRaycast(viewer, mouse, is3D, activeFloorId);
    if (fromEdgeHit) return fromEdgeHit;

    // Interior fallback: project mouse to the associated plane (floor for
    // rooms) via the line tool's existing coordinate transformer, then
    // point-in-polygon against each room's polygon.
    const worldPoint = coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) return null;

    for (const room of viewer.scene.rooms as PointoramaRoom[]) {
      if (!room.visible) continue;
      if (room.groupIdentifier !== activeFloorId) continue;
      const polygon = room.getPolygon();
      if (polygon.length < 3) continue;
      if (!this.isPointInPolygon(worldPoint, polygon)) continue;

      if (is3D) {
        const pointCloudHit = getPointCloudIntersection(viewer, mouse);
        if (pointCloudHit) {
          const distToPointCloud = camera.position.distanceTo(pointCloudHit);
          const distToRoom = camera.position.distanceTo(worldPoint);
          if (distToRoom > distToPointCloud * 1.1) continue;
        }
      }
      return room;
    }
    return null;
  }

  isMouseOverEditableShape(ctx: HitTestContext): boolean {
    return this.hitTestEditableTarget(ctx) !== null;
  }

  shouldRunSegmentationOnEnter(_shape: EditableShape): boolean {
    return true;
  }

  /**
   * Existing rooms on the active floor are already committed — depth
   * segmentation punches them out so they can't be detected again. The room
   * currently being edited stays segmentable.
   */
  getExcludedWorldPolygons(viewer: Viewer, editingShape: EditableShape | null): THREE.Vector3[][] {
    const activeFloorId = this.floorContext?.getGroupIdentifier() ?? null;
    const polygons: THREE.Vector3[][] = [];

    for (const room of viewer.scene.rooms as PointoramaRoom[]) {
      if (!room.visible) continue;
      if (room.groupIdentifier !== activeFloorId) continue;
      if ((room as EditableShape) === editingShape) continue;
      const polygon = room.getPolygon();
      if (polygon.length < 3) continue;
      polygons.push(polygon);
    }

    return polygons;
  }

  configureViewForShape(_shape: EditableShape, _viewer: Viewer): void {
    // Rooms edit on the same floor view the tool is already in.
  }

  restoreViewAfterShape(_shape: EditableShape, _viewer: Viewer): void {
    // Inverse of the no-op above.
  }

  /**
   * Builds the same `create_room` payload the inlined code at
   * AutomaticLineMode.createRoomFromShape and ManualLineMode's manual-finish
   * path produced. Bounding-box-relative image polygon, mask corners
   * clockwise from top-left, dummy plane fields with constants flipped from
   * heights — preserved verbatim for the existing client handler.
   */
  commitNew(viewer: Viewer, polygon: THREE.Vector3[], context: CommitContext): void {
    // Floor Z (initial) is the base plane's height; ceiling Z (initial) comes
    // from the floor selection context. These are placeholders that get
    // replaced by per-room detection over the polygon — the floor-group
    // defaults inherited from the active floor are usually close but not
    // always correct (sloped slabs, mezzanines, dropped ceilings inside
    // specific rooms).
    const groupFloorZ = context.basePlane.origin.z;
    const groupCeilingZ = this.floorContext?.getCeiling()?.height ?? groupFloorZ + 3.0;
    const floorGroupIdentifier = this.floorContext?.getGroupIdentifier() ?? null;

    // Detect the room's floor/ceiling over the WHOLE polygon (column
    // histogram pipeline — the dominant floor∩ceiling patch wins, so a shelf
    // or beam under one spot can't set the heights). The polygon itself is
    // USER INPUT and is never reshaped — detection only supplies heights,
    // falling back to the floor-group planes.
    const polygon2D = polygon.map((v) => new THREE.Vector2(v.x, v.y));
    const detected = detectRoomVolume(viewer.scene.pointclouds ?? [], polygon2D, groupFloorZ, groupCeilingZ);

    const floorZ = detected?.floorZ ?? groupFloorZ;
    const ceilingZ = detected?.ceilingZ ?? groupCeilingZ;

    const polygonPoints = polygon2D.map((v) => new THREE.Vector3(v.x, v.y, floorZ));

    const minX = Math.min(...polygonPoints.map((p) => p.x));
    const maxX = Math.max(...polygonPoints.map((p) => p.x));
    const minY = Math.min(...polygonPoints.map((p) => p.y));
    const maxY = Math.max(...polygonPoints.map((p) => p.y));

    const maskCorners = [new THREE.Vector3(minX, minY, floorZ), new THREE.Vector3(maxX, minY, floorZ), new THREE.Vector3(maxX, maxY, floorZ), new THREE.Vector3(minX, maxY, floorZ)];

    const imageWidth = maxX - minX;
    const imageHeight = maxY - minY;

    const imagePolygon = polygonPoints.map((p) => ({ x: p.x - minX, y: p.y - minY }));

    viewer.scene.dispatchEvent({
      type: 'create_room',
      value: {
        polygons: [{ points: imagePolygon }],
        maskCorners,
        imageWidth,
        imageHeight,
        detectedFloor: { distance: 0, plane: { normal: new THREE.Vector3(0, 0, 1), constant: -floorZ }, inlierCount: 0, points: [] as never[] },
        detectedCeiling: { distance: 0, plane: { normal: new THREE.Vector3(0, 0, 1), constant: -ceilingZ }, inlierCount: 0, points: [] as never[] },
        floorGroupIdentifier,
      },
    });
  }

  commitEdit(viewer: Viewer, shape: EditableShape, polygon: THREE.Vector3[]): void {
    // Apply the polygon to the room before dispatching: the `room_updated`
    // handler persists from `room.getPolygon()`, and on undo/redo restore the
    // caller passes the restored polygon (the room object itself hasn't been
    // mutated yet). For live edits this re-applies the already-current polygon
    // (harmless). setPolygon updates the visual via the room's own logic.
    if (polygon.length >= 3 && shape instanceof PointoramaRoom) {
      shape.setPolygon(polygon.map((p) => p.clone()));
    }
    viewer.scene.dispatchEvent({ type: 'room_updated', room: shape });
  }

  /** The MeasureSubTool engine may only edit rooms through this adapter. */
  canEditAnnotation(annotation: EditableAnnotation): boolean {
    return annotation instanceof PointoramaRoom;
  }

  /**
   * Flatten Z to floor height — rooms live on the floor plane. Was the
   * mode's inline behavior pre-adapter; now lives here so vertical-plane
   * kinds (door/window) can override without forking the modes.
   */
  snapPolygonToBasePlane(vertices: THREE.Vector3[], context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] {
    if (!context.basePlane) return vertices;
    const floorZ = context.basePlane.origin.z;
    return vertices.map((v) => new THREE.Vector3(v.x, v.y, floorZ));
  }

  /**
   * Horizontal frame at floor Z. Local (u, v) equals world (x, y) so
   * every XY-only snap module produces identical results to the
   * pre-frame behavior. Z-flatten — what used to be `wp.z = floorHeight`
   * — becomes `wp.z = 0` in local coords.
   */
  getBasePlaneFrame(context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): { origin: THREE.Vector3; u: THREE.Vector3; v: THREE.Vector3; normal: THREE.Vector3 } | null {
    if (!context.basePlane) return null;
    return {
      origin: new THREE.Vector3(0, 0, context.basePlane.origin.z),
      u: new THREE.Vector3(1, 0, 0),
      v: new THREE.Vector3(0, 1, 0),
      normal: new THREE.Vector3(0, 0, 1),
    };
  }

  /**
   * Rooms use the top-down ortho camera, so the legacy world-Z slab
   * derived from the floor-isolation plane is already tight along the
   * capture axis. No need to override.
   */
  getCaptureBoxCorners(_context: { viewer: Viewer; basePlane: BasePlaneState | null; shape: EditableShape | null }): THREE.Vector3[] | null {
    return null;
  }

  /**
   * Edge raycast against room background lines. In 3D, applies the same
   * 1.1× point-cloud occlusion the prior LineTool.getHoveredRoomInView
   * used. In 2D top-down ortho, occlusion is skipped — the cloud and rooms
   * sit at the same floor height so distance-based occlusion would
   * uniformly reject every room.
   */
  private findRoomFromEdgeRaycast(viewer: Viewer, mouse: THREE.Vector2, is3D: boolean, activeFloorId: string | null): PointoramaRoom | null {
    const roomLines = (viewer.scene.rooms as PointoramaRoom[]).filter((r) => r.visible && r.groupIdentifier === activeFloorId).map((r) => r.backgroundLine);

    const intersect = viewer.inputHandler.raycaster.intersectObjects(roomLines).at(0);
    if (!intersect) return null;

    if (is3D) {
      const pointCloudHit = getPointCloudIntersection(viewer, mouse);
      if (pointCloudHit) {
        const cameraPos = viewer.scene.getActiveCamera().position;
        const distToPointCloud = cameraPos.distanceTo(pointCloudHit);
        if (intersect.distance > distToPointCloud * 1.1) return null;
      }
    }

    let parent: (THREE.Object3D & { identifier?: string }) | null = intersect.object?.parent as (THREE.Object3D & { identifier?: string }) | null;
    while (parent && !parent.identifier) {
      parent = parent.parent as (THREE.Object3D & { identifier?: string }) | null;
    }
    return (parent as PointoramaRoom) || null;
  }

  private isPointInPolygon(point: THREE.Vector3, vertices: THREE.Vector3[]): boolean {
    let inside = false;
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      const xi = vertices[i].x,
        yi = vertices[i].y;
      const xj = vertices[j].x,
        yj = vertices[j].y;
      if (yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }
}
